/**
 * Shared Document Viewer — public page at /d/:token.
 *
 * Renders a frozen document snapshot entirely in the browser. The server only
 * returns JSON (GET /api/shared/:token) and validates the opaque token; it
 * never generates a PDF. "Export to PDF" is the browser's own print-to-PDF via
 * window.print() with a print stylesheet — works on desktop and mobile.
 *
 * One link, two behaviors:
 *  - Anonymous (subcontractor, no account) → sees the frozen snapshot.
 *  - Signed-in member of the document's company → also offered a link to open
 *    the live version inside KrewUp (can_view_full from the API).
 *
 * The global authenticated-fetch interceptor attaches the session token to this
 * request automatically when the opener has a session, which is what lets the
 * API decide can_view_full — no special handling needed here.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '@/lib/api-config';
import { getDocumentComponent, getDocumentAppRoute, type DocumentCapabilities } from './registry';
// Side-effect import: registers every concrete document type (renderers +
// in-app routes) into the registry before the viewer resolves one.
import './registerDocuments';

interface SharedResponse {
  success: boolean;
  doc_type: string;
  doc_id: string;
  // Capabilities are authoritative from the backend — the viewer only reacts
  // to them (e.g. show "Export to PDF" when pdf is true).
  capabilities: DocumentCapabilities;
  snapshot: { id: string; created_at: string; payload: any };
  viewer: { authenticated: boolean; can_view_full: boolean };
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; code: string; message: string }
  | { status: 'ready'; data: SharedResponse };

const ERROR_COPY: Record<string, string> = {
  not_available: 'Document sharing is not available yet. Please try again later.',
  not_found: 'This document could not be found. The link may be incorrect.',
  expired: 'This link has expired. Please request a new one.',
  revoked: 'This link has been revoked and is no longer available.',
  network: 'We could not load this document. Check your connection and try again.',
  internal: 'Something went wrong loading this document.',
  unsupported_renderer: 'This document type is temporarily unavailable',
};

export default function SharedDocumentViewer() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const printMode = searchParams.get('print') === '1';
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!token) {
        setState({ status: 'error', code: 'not_found', message: ERROR_COPY.not_found });
        return;
      }
      try {
        const res = await fetch(getApiUrl(`api/shared/${encodeURIComponent(token)}`));
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = body?.error || 'internal';
          if (!cancelled) {
            setState({ status: 'error', code, message: ERROR_COPY[code] || ERROR_COPY.internal });
          }
          return;
        }
        const data: SharedResponse = await res.json();
        if (!cancelled) setState({ status: 'ready', data });
      } catch {
        if (!cancelled) setState({ status: 'error', code: 'network', message: ERROR_COPY.network });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // If opened in print mode, trigger the browser's print dialog once the
  // document has rendered. Skip if we have no renderer to print.
  useEffect(() => {
    if (printMode && state.status === 'ready' && getDocumentComponent(state.data.doc_type)) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [printMode, state]);

  const handleExport = useCallback(() => window.print(), []);

  const DocComponent = useMemo(
    () => (state.status === 'ready' ? getDocumentComponent(state.data.doc_type) : null),
    [state]
  );

  // If the payload loaded but no renderer is registered for this docType, treat
  // it as a hard error: never render a partial/generic document. Log so we can
  // catch version drift (backend shipped a new type ahead of the frontend).
  useEffect(() => {
    if (state.status === 'ready' && DocComponent === null) {
      const docType = state.data.doc_type;
      console.error(
        `[SharedDocumentViewer] No renderer registered for docType="${docType}". ` +
          'Frontend build is missing this document type.'
      );
      // Best-effort telemetry for staff review; ignore if PostHog isn't loaded
      // (this route runs for unauthenticated recipients too).
      try {
        const w = window as unknown as { posthog?: { capture: (event: string, props?: unknown) => void } };
        w.posthog?.capture?.('shared_document_unsupported_renderer', {
          doc_type: docType,
          doc_id: state.data.doc_id,
        });
      } catch {
        // swallow — telemetry must never break the viewer
      }
    }
  }, [state, DocComponent]);

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--gray-100))' }}>
      {/* Print stylesheet: hide the toolbar/chrome and let the document fill the
          page when printing / exporting to PDF. */}
      <style>{`
        @media print {
          .kw-doc-toolbar { display: none !important; }
          .kw-doc-page { box-shadow: none !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
          body { background: rgb(var(--white)) !important; }
        }
        @page { margin: 0.5in; }
      `}</style>

      {/* Toolbar (hidden when printing) */}
      <div
        className="kw-doc-toolbar"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', background: 'rgb(var(--gray-900))', color: 'rgb(var(--white))', position: 'sticky', top: 0, zIndex: 'var(--z-sticky)' as unknown as number,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
          <span>KrewUp</span>
          {state.status === 'ready' && (
            <span style={{ fontSize: 12, color: 'rgb(var(--gray-400))', fontWeight: 400 }}>
              {state.data.doc_type.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {state.status === 'ready' && state.data.viewer.can_view_full && getDocumentAppRoute(state.data.doc_type) && (
            <a
              href={getDocumentAppRoute(state.data.doc_type) as string}
              style={{ padding: '8px 14px', background: 'rgb(var(--gray-700))', color: 'rgb(var(--white))', borderRadius: 'calc(var(--radius) - 2px)', textDecoration: 'none', fontSize: 14 }}
            >
              Open in KrewUp
            </a>
          )}
          {state.status === 'ready' && state.data.capabilities?.pdf && DocComponent && (
            <button
              onClick={handleExport}
              style={{ padding: '8px 14px', background: 'rgb(var(--info))', color: 'rgb(var(--white))', border: 'none', borderRadius: 'calc(var(--radius) - 2px)', cursor: 'pointer', fontSize: 14 }}
            >
              Export to PDF
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        {state.status === 'loading' && (
          <div style={{ color: 'rgb(var(--gray-500))', marginTop: 40 }}>Loading document…</div>
        )}

        {state.status === 'error' && (
          <div style={{ maxWidth: 420, marginTop: 60, textAlign: 'center', color: 'rgb(var(--gray-700))' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <p style={{ fontSize: 15 }}>{state.message}</p>
          </div>
        )}

        {state.status === 'ready' && DocComponent === null && (
          <div style={{ maxWidth: 460, marginTop: 60, textAlign: 'center', color: 'rgb(var(--gray-700))' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontSize: 15 }}>{ERROR_COPY.unsupported_renderer}</p>
            <p style={{ fontSize: 12, color: 'rgb(var(--gray-500))', marginTop: 8 }}>
              Document type: <code>{state.data.doc_type}</code>
            </p>
          </div>
        )}

        {state.status === 'ready' && DocComponent && (
          <div
            className="kw-doc-page"
            style={{ background: 'rgb(var(--white))', padding: 40, maxWidth: 880, width: '100%', boxShadow: 'var(--shadow-doc-page)', borderRadius: 'var(--radius)' }}
          >
            <DocComponent
              payload={state.data.snapshot.payload}
              docType={state.data.doc_type}
              docId={state.data.doc_id}
              mode={printMode ? 'print' : 'view'}
              capabilities={state.data.capabilities || {}}
            />
          </div>
        )}
      </div>
    </div>
  );
}
